import React, { useEffect, useState } from "react";
import { Badge, Box, Button, Flex, HStack, Spinner, Text, VStack, useToast } from "@chakra-ui/react";
import { PiFingerprintDuotone as FiKey, PiPlusBold as FiPlus } from "react-icons/pi";
import {
  Passkey,
  getPasskeys,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  deletePasskey
} from "@lubbezposrednio-frontend/core";
import { SecurityCard } from "./SecurityCard";
import { registerPasskey } from "./webauthnClient";

const ACCENT = "#6D28D9";

const supported = typeof window !== "undefined" && !!window.PublicKeyCredential;

export const PasskeyCard: React.FC = () => {
  const toast = useToast();
  const [passkeys, setPasskeys] = useState<Passkey[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await getPasskeys();
      setPasskeys(res.data);
    } catch {
      setPasskeys([]);
    }
  };

  useEffect(() => {
    if (supported) load();
    else setPasskeys([]);
  }, []);

  const add = async () => {
    setBusy(true);
    try {
      const { data } = await passkeyRegisterOptions();
      const credential = await registerPasskey(data.options);
      await passkeyRegisterVerify(credential, data.challenge_token);
      await load();
      toast({ title: "Passkey dodany ✓", status: "success", isClosable: true });
    } catch (e) {
      // NotAllowedError = user anulował/timeout — nie pokazujemy błędu jak za odrzucenie.
      const err = e as { name?: string; message?: string; response?: { data?: { errors?: string[] } } };
      // eslint-disable-next-line no-console
      console.error("[passkey] rejestracja nieudana:", err?.name, err?.message, e);
      if (err?.name !== "NotAllowedError") {
        const detail = err?.response?.data?.errors?.[0] ?? `${err?.name ?? "Błąd"}: ${err?.message ?? "nieznany"}`;
        toast({ title: "Passkey", description: detail, status: "error", duration: 9000, isClosable: true });
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    try {
      await deletePasskey(id);
      setPasskeys(p => (p ? p.filter(x => x.id !== id) : p));
      toast({ title: "Passkey usunięty.", status: "info", isClosable: true });
    } catch {
      toast({ title: "Nie udało się usunąć passkeya.", status: "error", isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <SecurityCard
      icon={FiKey}
      accent={ACCENT}
      full
      title="Passkey"
      subtitle="Logowanie bez hasła — odciskiem, twarzą lub kluczem sprzętowym. Odporne na phishing, kryptograficznie powiązane z tym serwisem."
      badge={(passkeys?.length ?? 0) > 0 ? <Badge colorScheme="purple" borderRadius="6px">{passkeys?.length}</Badge> : undefined}
      action={
        supported && (passkeys?.length ?? 0) > 0 ? (
          <Button variant="link" color={ACCENT} fontWeight="700" fontSize="14px" leftIcon={<FiPlus />} isDisabled={busy} onClick={add}>
            Dodaj kolejny
          </Button>
        ) : undefined
      }
    >
      {!supported ? (
        <Text fontSize="14px" color="gray.400">Ta przeglądarka nie obsługuje passkeyów.</Text>
      ) : passkeys === null ? (
        <Spinner size="sm" color="primary" />
      ) : passkeys.length === 0 ? (
        <Button leftIcon={<FiKey />} variant="solid" bg={ACCENT} color="white" borderRadius="12px" fontWeight="700" isLoading={busy} onClick={add} _hover={{ bg: "#5b21b6" }}>
          Dodaj passkey
        </Button>
      ) : (
        <VStack align="stretch" spacing="10px">
          {passkeys.map(p => (
            <Flex key={p.id} align="center" justify="space-between" p="12px 14px" borderRadius="12px" bg="rgba(109,40,217,0.04)" border="1px solid rgba(109,40,217,0.10)">
              <HStack spacing="12px" minW={0}>
                <Box color={ACCENT}><FiKey size={20} /></Box>
                <Box minW={0}>
                  <Text fontWeight="700" color="gray.700" noOfLines={1}>{p.nickname}</Text>
                  <Text fontSize="12px" color="gray.400">
                    {p.last_used_at ? "ostatnio użyty" : "dodany"} {new Date(p.last_used_at ?? p.created_at).toLocaleDateString("pl-PL")}
                  </Text>
                </Box>
              </HStack>
              <Button size="sm" variant="ghost" color="gray.500" fontWeight="700" isDisabled={busy} onClick={() => remove(p.id)} _hover={{ color: "#E51F44", bg: "rgba(229,31,68,0.06)" }}>
                Usuń
              </Button>
            </Flex>
          ))}
        </VStack>
      )}
    </SecurityCard>
  );
};
